import React from "react";
import { FormSectionElements, ProgramContainer } from "../../entities/formTemplateDataType";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Editor } from "primereact/editor";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";


interface Props {
    programContainer: ProgramContainer
    isEng: boolean;
    dialogVisible: boolean;
    callback; // proper typing?
}

interface State {
    sender: string;
    senderTouched: boolean;
    senderError: boolean;
    recipients: string[];
    recipientInput: string;
    recipientErrMsg: string;
    recipientTouched: boolean;
    subject: string;
    subjectTouched: boolean;
    body: string;
    noSender: boolean;
    noRecipients: boolean;
    noSubject: boolean;
    hasError: boolean;
}


export class DialogForEmailContents extends React.Component<Props, State> {
    fillEmailBodyEditor;
    constructor(props: Props) {
        super(props);
        this.state = {
            sender: props.programContainer.EmailSender || '',
            senderTouched: false,
            senderError: false,
            recipients: props.programContainer.EmailRecipients || [],
            recipientInput: '',
            recipientErrMsg: '',
            recipientTouched: false,
            subject: props.programContainer.EmailSubject || '',
            subjectTouched: false,
            body: props.programContainer.EmailBody || '',
            noSender: false,
            noRecipients: false,
            noSubject: false,
            hasError: false,
        };
    }

    // basic regex validation:
    //   returns true if there is non-empty email, '@', non-empty server name, '.', non-empty TLD in order
    isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    // validates + adds recipient email to array (upon key down, see line 188)
    addRecipient = (input: string) => {
        const email = input.toLowerCase().trim();

        // no feedback if passed empty value
        if (!email) {
            this.setState({ recipientErrMsg: '' });

        // checks if input is a valid email
        } else if (!this.isValidEmail(email)) {
            this.props.isEng ? this.setState({ recipientErrMsg: 'Invalid email format.' }) : this.setState({ recipientErrMsg: "Le format de l'adresse courriel est invalide." }); // check french

        // checks if input email already exists as a recipient
        } else if (this.state.recipients.includes(email)) {
            this.props.isEng ? this.setState({ recipientErrMsg: 'Email has already been added.' }) : this.setState({ recipientErrMsg: "L'adresse courriel est déjà ajoutée." }); // check french

        // otherwise, adds valid unique email to array of recipients
        // clears input field + error message
        } else {
            this.setState({ 
                recipients: [...this.state.recipients, email],
                recipientInput: '',
                recipientErrMsg: '',
                noRecipients: false,
            });
        }
    }

    // removes target recipient from array (upon button click, see line 164)
    removeRecipient = (email: string) => {
        this.setState({ recipients: this.state.recipients.filter(e => e !== email) });
    }

    generateContentString = () => {
        let htmlString = [];
        htmlString.push(
            <div style={{ width: "100%", paddingTop: "5px" }}>

                {/* sender field */}
                <div style={{ display: "flex", marginTop: '10px' }}>
                    <div style={{ width: '30%' }}>
                        <label htmlFor={"tbl_EmailSender_" + this.props.programContainer.id}>Sender Address</label>
                    </div>

                    <div style={{ width: '60%' }}>   
                        <InputText 
                            style={{ width: '100%' }}
                            id={"tbl_EmailSender_" + this.props.programContainer.id}
                            value={this.state.sender} 
                        
                            // on change: clears "invalid sender" error message
                            //            rerenders sender value
                            onChange={(e) => {
                                this.setState({ 
                                    sender: e.target.value,
                                    senderTouched: false,
                                    senderError: false,
                                    noSender: false,
                                });
                            }}

                            // on blur: renders "invalid sender" error message if necessary
                            onBlur={(e) => {
                                const error = this.state.sender && !this.isValidEmail(this.state.sender)
                                this.setState({ 
                                    senderTouched: true,
                                    senderError: error ? true : false,
                                });
                            }}
                        />
                        
                        {/* invalid sender error message */}
                        {this.state.senderTouched && this.state.senderError && (
                            <div style={{ color: 'red', marginTop: '4px' }}>
                                {this.props.isEng
                                    ? 'Invalid email format.'
                                    : "Le format de l'adresse courriel est invalide." // check french
                                }
                            </div>
                        )}

                        {/* no sender error message */}
                        {this.state.noSender && (
                            <div style={{ color: 'red', marginTop: '4px' }}>
                                {this.props.isEng
                                    ? 'Sender address is required.'
                                    : "Adresse de l'expéditeur est obligatoire." // check french
                                }
                            </div>
                        )}
                    </div>
                </div>

                {/* recipients field */}
                <div style={{ display: 'flex', marginTop: '10px' }}>
                    <div style={{ width: '30%' }}>
                        <label htmlFor={"tbl_EmailRecipients_" + this.props.programContainer.id}>Recipient Addresses</label>
                    </div>

                    <div style={{ width: '60%' }}>
                        <div 
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '5px',
                            }}
                        >
                            {/* recipient pills */}
                            {this.state.recipients.map((email, index) => (
                                <div 
                                    key={index} 
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        background: '#e0e0e0',
                                        borderRadius: '10px',
                                        padding: '4px 8px',
                                        fontSize: '0.85em',
                                        maxWidth: '100%',
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    <span style={{ marginRight: '6px' }}>{email}</span>

                                    {/* x button to remove recipient */}
                                    <Button 
                                        icon='pi pi-times'
                                        style={{
                                            alignItems: 'center',
                                            background: 'none',
                                            border: 'none',
                                            color: '#555',
                                            padding: '2px',
                                            width: '15px',
                                            height: '15px',
                                            fontSize: '0.3rem',
                                        }}

                                        // on click: removes target recipient
                                        //           clears "invalid recipient" error message
                                        onClick={() => {
                                            this.removeRecipient(email)
                                            this.setState({ recipientTouched: false });
                                        }}
                                    />
                                </div>
                            ))}

                            <InputText
                                style={{ width: '100%' }}
                                id={"tbl_EmailRecipients_" + this.props.programContainer.id}
                                value={this.state.recipientInput}
                                placeholder={this.props.isEng ? "Type email and press Enter or Space to add" : "Tapez l'e-mail et appuyez sur Entrée ou Espace pour l'ajouter"} // check french
                                
                                // on change: rerenders field value
                                //            clears error message 
                                onChange={(e) => {
                                    this.setState({ 
                                        recipientInput: e.target.value,
                                        recipientTouched: false,
                                    });
                                }}

                                // on Enter or Space key: runs validation on input
                                //                        adds as recipient if valid, renders "invalid recipient" error message otherwise
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        this.addRecipient(this.state.recipientInput);
                                        this.setState({ recipientTouched: true });
                                    }
                                }}
                            />

                            {/* invalid recipient error message */}
                            {this.state.recipientErrMsg && this.state.recipientTouched && (
                                <div style={{ color: 'red', marginTop: '4px' }}>
                                    {this.state.recipientErrMsg}
                                </div>
                            )}

                            {/* empty recipient list error message */}
                            {this.state.noRecipients && (
                                <div style={{ color: 'red', marginTop: '4px' }}>
                                    {this.props.isEng
                                        ? 'At least one recipient is required.'
                                        : "Au moins un destinataire est obligatoire." // check french
                                    }
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* subject field */}
                <div style={{ display: "flex", marginTop: '10px' }}>
                    <div style={{ width: '30%' }}>
                        <label htmlFor={"tbl_EmailSubject_" + this.props.programContainer.id}>Subject</label>
                    </div>

                    <div style={{ width: '60%' }}>
                        <InputText 
                            style={{ width: '100%' }}
                            id={"tbl_EmailSubject_" + this.props.programContainer.id}
                            value={this.state.subject} 
                            
                            onChange={(e) => {
                                this.setState({ 
                                    subject: e.target.value,
                                    subjectTouched: false,
                                    noSubject: false,
                                });
                            }}

                            onBlur={(e) => {
                                this.setState({ subjectTouched: true });
                            }}
                        />

                        {/* no subject error message */}
                        {this.state.noSubject && (
                            <div style={{ color: 'red', marginTop: '4px' }}>
                                {this.props.isEng
                                    ? 'Subject is required.'
                                    : "Le sujet est obligatoire." // check french
                                }
                            </div>
                        )}
                    </div>
                </div>
                
                {/* body field */}
                <div style={{ display: "flex" ,marginTop: '10px' }}>
                    <div style={{ width: '30%' }}>
                        <label htmlFor={"tbl_EmailBody_" + this.props.programContainer.id}>Body</label>
                    </div>

                <div style={{ width: '60%' }}>
                {/* @ts-ignore */}
                {this.fillEmailBodyEditor && (                                
                     <Editor 
                            style={{ height: 'auto', width: '100%' }}
                            id={"tbl_EmailBody_" + this.props.programContainer.id}
                            value={this.state.body}                         
                            onTextChange={(e) => {
                                this.setState({ body: e.htmlValue });
                                this.props.callback();
                        }}
                     />
                 )}                                                    
                 {!this.fillEmailBodyEditor && (
                     <InputTextarea
                          className="ontario-input"
                           id={"tbl_EmailBody_" + this.props.programContainer.id}
                           value={this.state.body}
                           onChange={(e) => {
                                this.setState({ body: e.target.value });
                           }}
                       /> 
                )}
                 <button onClick={() => {
                     this.fillEmailBodyEditor = !this.fillEmailBodyEditor;
                     this.props.callback();
                 }}>
                  {this.fillEmailBodyEditor == true ? "Raw" : "HTML"}
                </button>
                </div>
                </div>
            </div>
        );
        return htmlString;
    };
     InitialVisibility = () => {
          return (
              <div style={{ display: "flex", marginTop: '10px' }}>
                  <div style={{ width: '25%' }}>
                        {/* @ts-ignore */}
                      <label htmlFor={"show_optional_label_" + this.props.ele.Sequence}>Invisible by Default:</label>
                  </div>
                  <div style={{ width: '60%' }}>
                        {/* @ts-ignore */}
                      <Checkbox id={"show_optional_label_" + this.props.ele.Sequence} checked={this.props.ele.InvisibleByDefault ?? false} value={this.props.ele.InvisibleByDefault ?? false} title="When checked, this field will be invisible by default." onChange={(e) => { this.props.ele.InvisibleByDefault = e.checked; this.props.callback();
                          }
                          }
                      />
                  </div>
              </div>
          )
      }
    // resets state with latest data from programContainer and clears error messages when dialog becomes visible
    componentDidUpdate(prevProps: Props) {
        if (!prevProps.dialogVisible && this.props.dialogVisible) {
            this.setState({
                sender: this.props.programContainer.EmailSender || '',
                senderError: false,
                recipients: this.props.programContainer.EmailRecipients || [],
                recipientErrMsg: '',
                subject: this.props.programContainer.EmailSubject || '',
                body: this.props.programContainer.EmailBody || '',
                noSender: false,
                noRecipients: false,
                noSubject: false,
                hasError: false,
            });
        }
    }

    // footer with 'OK' button to save and 'Cancel' button to discard changes
    renderFooter = () => {
        return (
            <div>
                {/* 'OK' button */ }
                <Button 
                    label="OK" 
                    icon="pi pi-check" 
                    onClick={() => { 
                        let missingFields = false;

                        // checks for missing required attributes (sender, recipient, subject) and renders respective error messages
                        if (!this.state.sender) {
                            this.setState({ noSender: true });
                            missingFields = true;
                        } 
                        if (!this.state.recipients.length) {
                            this.setState({ noRecipients: true });
                            missingFields = true;
                        }
                        if (!this.state.subject) {
                            this.setState({ noSubject: true });
                            missingFields = true;
                        }

                        // checks for unresolved errors and renders "unresolved errors" error message
                        if (missingFields || this.state.senderError) {
                            this.setState({ hasError: true });


                        // updates valid inputs and closes dialogue
                        } else {
                            this.props.programContainer.EmailSender = this.state.sender;
                            this.props.programContainer.EmailRecipients = this.state.recipients;
                            this.props.programContainer.EmailSubject = this.state.subject;
                            this.props.programContainer.EmailBody = this.state.body ? this.state.body : ' '; // sendgrid does not allow empty content or subject
                            this.props.callback(false);
                        }
                    }}
                />

                {/* 'Cancel' button */}
                <Button 
                    label="Cancel"
                    onClick={() => { 
                        this.setState({ recipientInput: '' });
                        this.props.callback(false);
                    }} 
                />

                {/* unresolved errors error message */}
                {this.state.hasError && (
                    <div style={{ color: 'red', marginTop: '4px' }}>
                        {this.props.isEng
                            ? 'There are unresolved errors.'
                            : "Il y a des erreurs non résolues." // check french
                        }
                    </div>
                )}
            </div>
        );
    }

    generateHtmlString = () => {
        let html = [];
        html.push(
            <>
                <Dialog 
                    header={this.props.isEng ? "Email contents" : "Contenu du courriel"} 
                    footer={this.renderFooter()}
                    visible={this.props.dialogVisible} 
                    style={{ width: '50vw' }} 
                    onHide={() => this.props.callback(false)}
                >
                    {this.generateContentString()}
                </Dialog>
            </>
        );
        return html;
    }

    render() {
        return (
            <div>
                {this.generateHtmlString()}
            </div>
        );
    }
}